import React, { useState } from "react";
import "./DesignQuestionsCard.css";

/**
 * Extract structured questions from the AI's plan response text.
 * Looks for numbered lists (1. ...) or bullet points (- ...) that end with ?
 *
 * Returns: [{ id, label, question, options }]
 * - label: bold prefix if present (e.g., "Kalshi API key")
 * - question: the full question text
 * - options: extracted choices if the AI offered them (e.g., "top 3 or top 5")
 */
export function extractQuestions(text) {
  if (!text) return [];

  const questions = [];
  // Match numbered items (1. ...) or bullet items (- ..., * ...) that contain a question mark
  const lines = text.split("\n");
  let currentQ = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Match numbered list items: "1. **Bold label** — question text?"
    // Or bullet items: "- **Bold label**: question text?"
    const listMatch = trimmed.match(/^(?:\d+\.\s*|\*\s*|-\s*)(?:\*\*([^*]+)\*\*\s*(?:—|:|–|-)\s*)?(.+)/);

    if (listMatch) {
      const label = listMatch[1]?.trim() || null;
      const body = listMatch[2].trim();

      // Only include if it contains a question mark
      if (body.includes("?")) {
        // Try to extract options from the question (e.g., "Would you prefer X or Y?")
        const options = extractOptions(body);

        currentQ = {
          id: `q${questions.length}`,
          label,
          question: body,
          options,
        };
        questions.push(currentQ);
      }
    }
  }

  // Fallback: if no structured questions found, look for sentences ending with ?
  // This catches paragraph-style questions the AI sometimes generates
  if (questions.length === 0) {
    const sentenceQuestions = text.match(/[^.!?\n]{15,}?\?/g);
    if (sentenceQuestions) {
      for (const sq of sentenceQuestions) {
        const cleaned = sq.trim();
        // Skip generic "does this look right?" type questions
        if (/^(does|do) (this|the) (plan|look)/i.test(cleaned)) continue;
        if (/would you like to (adjust|change|modify) anything/i.test(cleaned)) continue;

        questions.push({
          id: `q${questions.length}`,
          label: null,
          question: cleaned,
          options: extractOptions(cleaned),
        });
      }
    }
  }

  return questions.slice(0, 5); // Cap at 5 questions
}

/**
 * Extract choice options from a question string.
 * e.g., "Would you prefer a top-3 leaderboard or just the #1 winner?" → ["top-3 leaderboard", "just the #1 winner"]
 */
function extractOptions(question) {
  // Pattern: "X or Y?" at the end
  const orMatch = question.match(/(?:prefer|like|want)\s+(.+?)\s+or\s+(.+?)\??$/i);
  if (orMatch) {
    return [orMatch[1].replace(/^(a|an|the)\s+/i, "").trim(), orMatch[2].replace(/\??$/, "").trim()];
  }
  return null;
}

/**
 * DesignQuestionsCard — structured question card shown inline in the chat.
 * Forces user to answer each question before the plan is finalized.
 */
export default function DesignQuestionsCard({ questions, onSubmit }) {
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(questions.map((q) => [q.id, ""]))
  );
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = questions.every((q) => answers[q.id]?.trim());

  const handleOptionClick = (qId, option) => {
    setAnswers((prev) => ({ ...prev, [qId]: option }));
  };

  const handleSubmit = () => {
    if (!allAnswered) return;
    setSubmitted(true);
    onSubmit(
      questions.map((q) => ({
        question: q.label || q.question,
        answer: answers[q.id],
      }))
    );
  };

  if (submitted) {
    return (
      <div className="dq-card dq-submitted">
        <div className="dq-header">
          <span className="dq-icon">&#10003;</span>
          <span>Design choices submitted</span>
        </div>
        <div className="dq-answers-summary">
          {questions.map((q) => (
            <div key={q.id} className="dq-answer-row">
              <span className="dq-answer-label">{q.label || "Q"}</span>
              <span className="dq-answer-value">{answers[q.id]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dq-card">
      <div className="dq-header">
        <span className="dq-icon">&#128396;</span>
        <span>A few design choices before I build</span>
      </div>
      <div className="dq-questions">
        {questions.map((q, i) => (
          <div key={q.id} className="dq-question">
            <div className="dq-question-text">
              <span className="dq-question-num">{i + 1}</span>
              {q.label && <strong>{q.label}: </strong>}
              <span>{q.question}</span>
            </div>
            {q.options ? (
              <div className="dq-options">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    className={`dq-option ${answers[q.id] === opt ? "selected" : ""}`}
                    onClick={() => handleOptionClick(q.id, opt)}
                  >
                    {opt}
                  </button>
                ))}
                <input
                  className="dq-custom-input"
                  placeholder="Or type your own..."
                  value={q.options.includes(answers[q.id]) ? "" : answers[q.id]}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                />
              </div>
            ) : (
              <input
                className="dq-text-input"
                placeholder="Type your answer..."
                value={answers[q.id]}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      <button
        className={`dq-submit ${allAnswered ? "" : "disabled"}`}
        onClick={handleSubmit}
        disabled={!allAnswered}
      >
        Build with these choices
      </button>
    </div>
  );
}
